import React, { useState } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import PageContainer from '../components/common/PageContainer';
import PageHeader from '../components/common/PageHeader';
import PageSection from '../components/common/PageSection';

const TherapistAlerts: React.FC = () => {
    const { user } = useAuth();
    const [filter, setFilter] = useState<string>('all');
    const [showEmergencyModal, setShowEmergencyModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [showCreateAlertModal, setShowCreateAlertModal] = useState(false);

    // Mock alert data
    const alerts = [
        {
            id: 1,
            type: 'High Risk',
            patient: 'Sarah Johnson',
            message: 'Patient reported suicidal ideation during last session',
            timestamp: '2 minutes ago',
            priority: 'Critical',
            status: 'Unread'
        },
        {
            id: 2,
            type: 'Missed Session',
            patient: 'Mike Chen',
            message: 'Patient missed scheduled appointment without notice',
            timestamp: '1 hour ago',
            priority: 'High',
            status: 'Unread'
        },
        {
            id: 3,
            type: 'Progress Update',
            patient: 'Emma Davis',
            message: 'Patient completed wellness exercise and reported positive mood',
            timestamp: '3 hours ago',
            priority: 'Low',
            status: 'Read'
        },
        {
            id: 4,
            type: 'Medication Alert',
            patient: 'Alex Rodriguez',
            message: 'Patient reported side effects from new medication',
            timestamp: '5 hours ago',
            priority: 'Medium',
            status: 'Read'
        },
        {
            id: 5,
            type: 'Emergency Contact',
            patient: 'Sarah Johnson',
            message: 'Emergency contact called regarding patient safety concerns',
            timestamp: '1 day ago',
            priority: 'Critical',
            status: 'Read'
        }
    ];

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'Critical': return 'text-red-600 bg-red-100 border-red-200';
            case 'High': return 'text-orange-600 bg-orange-100 border-orange-200';
            case 'Medium': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
            case 'Low': return 'text-green-600 bg-green-100 border-green-200';
            default: return 'text-gray-600 bg-gray-100 border-gray-200';
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'High Risk': return '🚨';
            case 'Missed Session': return '❌';
            case 'Progress Update': return '📈';
            case 'Medication Alert': return '💊';
            case 'Emergency Contact': return '📞';
            default: return '🔔';
        }
    };

    const unreadCount = alerts.filter(alert => alert.status === 'Unread').length;
    const criticalCount = alerts.filter(alert => alert.priority === 'Critical').length;

    // Filter alerts based on selected filter
    const filteredAlerts = alerts.filter(alert => {
        switch (filter) {
            case 'critical':
                return alert.priority === 'Critical';
            case 'high':
                return alert.priority === 'High';
            case 'medium':
                return alert.priority === 'Medium';
            case 'low':
                return alert.priority === 'Low';
            case 'unread':
                return alert.status === 'Unread';
            default:
                return true; // 'all'
        }
    });

    return (
        <PageContainer>
            <PageHeader 
                title="Alerts & Notifications" 
                emoji="🔔"
                description="Monitor patient alerts and system notifications"
            />
            
            {/* Alert Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl mb-3">📬</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Unread Alerts</h3>
                    <div className="text-4xl font-bold text-red-600 mb-2">{unreadCount}</div>
                    <div className="text-sm text-gray-600">Require Attention</div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl mb-3">🚨</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Critical Alerts</h3>
                    <div className="text-4xl font-bold text-red-600 mb-2">{criticalCount}</div>
                    <div className="text-sm text-gray-600">Urgent Priority</div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl mb-3">📅</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Alerts</h3>
                    <div className="text-4xl font-bold text-blue-600 mb-2">8</div>
                    <div className="text-sm text-gray-600">Total Today</div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-6 text-center">
                    <div className="text-3xl mb-3">✅</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Resolved</h3>
                    <div className="text-4xl font-bold text-green-600 mb-2">12</div>
                    <div className="text-sm text-gray-600">This Week</div>
                </div>
            </div>

            {/* Alert Filters */}
            <PageSection title="Filter Alerts" emoji="🔍">
                <div className="flex flex-wrap gap-2">
                    <button 
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            filter === 'all' 
                                ? 'bg-gray-200 text-gray-800' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        All ({alerts.length})
                    </button>
                    <button 
                        onClick={() => setFilter('critical')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            filter === 'critical' 
                                ? 'bg-red-200 text-red-800' 
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                    >
                        Critical ({criticalCount})
                    </button>
                    <button 
                        onClick={() => setFilter('high')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            filter === 'high' 
                                ? 'bg-orange-200 text-orange-800' 
                                : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                    >
                        High Priority
                    </button>
                    <button 
                        onClick={() => setFilter('medium')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            filter === 'medium' 
                                ? 'bg-yellow-200 text-yellow-800' 
                                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                    >
                        Medium Priority
                    </button>
                    <button 
                        onClick={() => setFilter('low')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            filter === 'low' 
                                ? 'bg-green-200 text-green-800' 
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                    >
                        Low Priority
                    </button>
                    <button 
                        onClick={() => setFilter('unread')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            filter === 'unread' 
                                ? 'bg-blue-200 text-blue-800' 
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                    >
                        Unread Only ({unreadCount})
                    </button>
                </div>
            </PageSection>

            {/* Alert List */}
            <PageSection title="Recent Alerts" emoji="📋">
                <div className="space-y-4">
                    {filteredAlerts.map((alert) => (
                        <div key={alert.id} className={`p-4 border-l-4 rounded-lg ${getPriorityColor(alert.priority)} ${alert.status === 'Unread' ? 'bg-blue-50' : 'bg-white'}`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3">
                                    <span className="text-2xl">{getTypeIcon(alert.type)}</span>
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <h3 className="font-semibold text-gray-900">{alert.type}</h3>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(alert.priority)}`}>
                                                {alert.priority}
                                            </span>
                                            {alert.status === 'Unread' && (
                                                <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                            )}
                                        </div>
                                        <p className="text-gray-700 mb-2">{alert.message}</p>
                                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                                            <span>Patient: <strong>{alert.patient}</strong></span>
                                            <span>{alert.timestamp}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex space-x-2">
                                    <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm">
                                        View Details
                                    </button>
                                    <button className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors text-sm">
                                        Mark Read
                                    </button>
                                    <button className="px-3 py-1 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors text-sm">
                                        Take Action
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </PageSection>

            {/* Quick Actions */}
            <PageSection title="Quick Actions" emoji="⚡">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button 
                        onClick={() => setShowEmergencyModal(true)}
                        className="p-4 bg-red-100 hover:bg-red-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">🚨</span>
                            <div>
                                <div className="font-medium text-gray-900">Emergency Protocol</div>
                                <div className="text-sm text-gray-600">Access emergency procedures</div>
                            </div>
                        </div>
                    </button>
                    <button 
                        onClick={() => setShowContactModal(true)}
                        className="p-4 bg-blue-100 hover:bg-blue-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">📞</span>
                            <div>
                                <div className="font-medium text-gray-900">Contact Patient</div>
                                <div className="text-sm text-gray-600">Reach out to patients</div>
                            </div>
                        </div>
                    </button>
                    <button 
                        onClick={() => setShowCreateAlertModal(true)}
                        className="p-4 bg-green-100 hover:bg-green-200 rounded-lg text-left transition-colors"
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-2xl">📋</span>
                            <div>
                                <div className="font-medium text-gray-900">Create Alert</div>
                                <div className="text-sm text-gray-600">Add new alert or note</div>
                            </div>
                        </div>
                    </button>
                </div>
            </PageSection>

            {/* Emergency Protocol Modal */}
            {showEmergencyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-xl font-semibold text-gray-900">Emergency Protocol</h3>
                            <button
                                onClick={() => setShowEmergencyModal(false)}
                                className="text-gray-600 hover:text-gray-800 text-2xl bg-transparent hover:bg-gray-100 rounded p-1"
                            >
                                ×
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                <h4 className="font-semibold text-red-800 mb-2">Immediate Actions Required</h4>
                                <ul className="list-disc list-inside text-red-700 space-y-1">
                                    <li>Call 911 if there's immediate danger to self or others</li>
                                    <li>Contact emergency services: 988 (Suicide & Crisis Lifeline)</li>
                                    <li>Notify patient's emergency contact immediately</li>
                                    <li>Document all actions taken in patient record</li>
                                </ul>
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                <h4 className="font-semibold text-yellow-800 mb-2">Follow-up Actions</h4>
                                <ul className="list-disc list-inside text-yellow-700 space-y-1">
                                    <li>Schedule immediate follow-up session</li>
                                    <li>Review safety plan with patient</li>
                                    <li>Consider hospitalization if necessary</li>
                                    <li>Update treatment plan based on crisis</li>
                                </ul>
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowEmergencyModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => {
                                        alert('Emergency protocol activated - this would trigger real emergency procedures');
                                        setShowEmergencyModal(false);
                                    }}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    Activate Protocol
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Patient Modal */}
            {showContactModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-xl font-semibold text-gray-900">Contact Patient</h3>
                            <button
                                onClick={() => setShowContactModal(false)}
                                className="text-gray-600 hover:text-gray-800 text-2xl bg-transparent hover:bg-gray-100 rounded p-1"
                            >
                                ×
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Select Patient</label>
                                <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900">
                                    <option>Sarah Johnson</option>
                                    <option>Mike Chen</option>
                                    <option>Emma Davis</option>
                                    <option>Alex Rodriguez</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Method</label>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input type="radio" name="contactMethod" value="phone" className="mr-2" />
                                        <span>Phone Call</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="radio" name="contactMethod" value="email" className="mr-2" />
                                        <span>Email</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="radio" name="contactMethod" value="text" className="mr-2" />
                                        <span>Text Message</span>
                                    </label>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Message (Optional)</label>
                                <textarea
                                    rows={4}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                                    placeholder="Add a brief message for the patient..."
                                ></textarea>
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowContactModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        alert('Contact initiated - this would send the actual message');
                                        setShowContactModal(false);
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Send Contact
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Alert Modal */}
            {showCreateAlertModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-xl font-semibold text-gray-900">Create New Alert</h3>
                            <button
                                onClick={() => setShowCreateAlertModal(false)}
                                className="text-gray-600 hover:text-gray-800 text-2xl bg-transparent hover:bg-gray-100 rounded p-1"
                            >
                                ×
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Patient</label>
                                <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900">
                                    <option>Sarah Johnson</option>
                                    <option>Mike Chen</option>
                                    <option>Emma Davis</option>
                                    <option>Alex Rodriguez</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Alert Type</label>
                                <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900">
                                    <option>High Risk</option>
                                    <option>Missed Session</option>
                                    <option>Progress Update</option>
                                    <option>Medication Alert</option>
                                    <option>Emergency Contact</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Priority Level</label>
                                <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900">
                                    <option>Critical</option>
                                    <option>High</option>
                                    <option>Medium</option>
                                    <option>Low</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Alert Message</label>
                                <textarea
                                    rows={4}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                                    placeholder="Describe the alert details..."
                                ></textarea>
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowCreateAlertModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        alert('Alert created - this would save the alert to the system');
                                        setShowCreateAlertModal(false);
                                    }}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                >
                                    Create Alert
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PageContainer>
    );
};

export default TherapistAlerts;
